import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';
import {
  CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY,
  CATEGORY_LAYOUT_PHONE_MAX_PX,
} from './category-layout.util';
import {
  AllProductsViewPorts,
  DEFAULT_ALL_PRODUCTS_VIEW_PORTS,
  isViewPortKey,
  ViewPortKey,
  VIEW_PORT_KEYS,
} from './our-products-layout.util';
import { resolveMlText } from './products-page-layout.util';

export const CUSTOMER_DASHBOARD_PAYLOAD_KEY = 'customerDashboard';
/** Mobile flexible-screen dashboard config; falls back to desktop when absent. */
export const CUSTOMER_DASHBOARD_MOBILE_PAYLOAD_KEY = 'customerDashboardMobile';

export type CustomerDashboardLayoutDevice = 'desktop' | 'mobile';

export {
  CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY,
  CATEGORY_LAYOUT_PHONE_MAX_PX as CUSTOMER_DASHBOARD_LAYOUT_PHONE_MAX_PX,
};

export function resolveCustomerDashboardLayoutDeviceFromViewport(
  win: Window | null = typeof window !== 'undefined' ? window : null,
): CustomerDashboardLayoutDevice {
  if (!win?.matchMedia) {
    return 'desktop';
  }
  return win.matchMedia(CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY).matches ? 'mobile' : 'desktop';
}

export type DashboardKpiKey = 'orders' | 'shipments' | 'newItems';

export type MonthOrderFilterType = 'orders' | 'invoices' | 'shipments';

const DEFAULT_KPI_ORDER: DashboardKpiKey[] = ['orders', 'shipments', 'newItems'];

const KPI_DEFAULT_TITLE_KEYS: Record<DashboardKpiKey, string> = {
  orders: 'CFDashboard.AllOrders',
  shipments: 'ADCustomerCustomize.totalShipments',
  newItems: 'CFDashboard.NewProducts',
};

const KPI_DEFAULT_SOURCES: Record<DashboardKpiKey, string> = {
  orders: 'CFDashboard.AllOrders',
  shipments: 'ADCustomerCustomize.totalShipments',
  newItems: 'CFDashboard.NewProducts',
};

const MONTH_ORDER_ITEMS_OPTIONS = [3, 6, 9];

const DEFAULT_ITEMS_VIEW_PORTS: AllProductsViewPorts = {
  ...DEFAULT_ALL_PRODUCTS_VIEW_PORTS,
};

export interface CustomerDashboardLayoutConfig {
  visible: boolean;
  kpiOrder: DashboardKpiKey[];
  titles: {
    orders: string | null;
    shipments: string | null;
    newItems: string | null;
    monthOrder: string | null;
    calendar: string | null;
    items: string | null;
  };
  newItemsCtaEnabled: boolean;
  /** Title source key for the New Items KPI (e.g. CFDashboard.NewProducts). */
  newItemsTitleSource: string;
  /** Title source keys for each KPI card. */
  kpiTitleSources: Record<DashboardKpiKey, string>;
  /** CTA enabled flags for each KPI card (used when title source is Products). */
  kpiCtaEnabled: Record<DashboardKpiKey, boolean>;
  monthOrderFilterType: MonthOrderFilterType;
  monthOrderItemsDisplayed: number;
  itemsEnabledViewPorts: AllProductsViewPorts;
  itemsDefaultViewPort: ViewPortKey;
  itemsViewPortOrder: ViewPortKey[];
}

function isKpiKey(value: unknown): value is DashboardKpiKey {
  return value === 'orders' || value === 'shipments' || value === 'newItems';
}

function isMonthOrderFilterType(value: unknown): value is MonthOrderFilterType {
  return value === 'orders' || value === 'invoices' || value === 'shipments';
}

function readDesktopRoot(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const root = payload[CUSTOMER_DASHBOARD_PAYLOAD_KEY];
  if (!root || typeof root !== 'object' || Array.isArray(root)) {
    return null;
  }
  return root as Record<string, unknown>;
}

function readRoot(
  payload: Record<string, unknown> | null | undefined,
  device: CustomerDashboardLayoutDevice = 'desktop',
): Record<string, unknown> | null {
  if (device === 'desktop') {
    return readDesktopRoot(payload);
  }
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const mobile = payload[CUSTOMER_DASHBOARD_MOBILE_PAYLOAD_KEY];
  if (mobile && typeof mobile === 'object' && !Array.isArray(mobile)) {
    return mobile as Record<string, unknown>;
  }
  return readDesktopRoot(payload);
}

/** Wraps a dashboard root so admin parsers that expect `customerDashboard` can read mobile slices. */
export function customerDashboardPayloadForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: CustomerDashboardLayoutDevice,
): Record<string, unknown> | null | undefined {
  if (!payload || device === 'desktop') {
    return payload;
  }
  const root = readRoot(payload, 'mobile');
  if (!root) {
    return payload;
  }
  return { [CUSTOMER_DASHBOARD_PAYLOAD_KEY]: root };
}

function parseMlRecord(value: unknown): MlTextRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const record: MlTextRecord = {};
  for (const [key, text] of Object.entries(value as Record<string, unknown>)) {
    if (typeof text === 'string') {
      record[key] = text;
    }
  }
  return record;
}

function resolveTitle(
  titleMl: unknown,
  titleSource: unknown,
  fallbackKey: string,
  lang: string,
  translate: (key: string) => string,
): string | null {
  const typed = resolveMlText(parseMlRecord(titleMl), lang);
  if (typed) {
    return typed;
  }
  const source = typeof titleSource === 'string' ? titleSource.trim() : '';
  if (source && source !== 'custom') {
    const fromSource = (translate(source) || '').trim();
    if (fromSource) {
      return fromSource;
    }
  }
  const fallback = (translate(fallbackKey) || '').trim();
  return fallback || null;
}

function parseKpiOrder(kpisRecord: Record<string, unknown>): DashboardKpiKey[] {
  const raw = kpisRecord['order'];
  if (!Array.isArray(raw)) {
    return [...DEFAULT_KPI_ORDER];
  }
  const parsed = raw.filter(isKpiKey);
  return [...parsed, ...DEFAULT_KPI_ORDER.filter((key) => !parsed.includes(key))];
}

function parseItemsViewPorts(stored: unknown): {
  enabled: AllProductsViewPorts;
  order: ViewPortKey[];
  defaultKey: ViewPortKey;
} {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return {
      enabled: { ...DEFAULT_ITEMS_VIEW_PORTS },
      order: [...VIEW_PORT_KEYS],
      defaultKey: 'verticalCard',
    };
  }
  const record = stored as Record<string, unknown>;
  const enabled: AllProductsViewPorts = {
    verticalCard:
      typeof record['verticalCard'] === 'boolean'
        ? record['verticalCard']
        : DEFAULT_ITEMS_VIEW_PORTS.verticalCard,
    horizontalCard:
      typeof record['horizontalCard'] === 'boolean'
        ? record['horizontalCard']
        : DEFAULT_ITEMS_VIEW_PORTS.horizontalCard,
    tableView:
      typeof record['tableView'] === 'boolean'
        ? record['tableView']
        : DEFAULT_ITEMS_VIEW_PORTS.tableView,
  };
  if (!enabled.verticalCard && !enabled.horizontalCard && !enabled.tableView) {
    enabled.verticalCard = true;
  }

  const rawOrder = record['order'];
  const parsedOrder = Array.isArray(rawOrder) ? rawOrder.filter(isViewPortKey) : [];
  const order = [...parsedOrder, ...VIEW_PORT_KEYS.filter((key) => !parsedOrder.includes(key))];

  const rawDefault = record['default'];
  let defaultKey: ViewPortKey = isViewPortKey(rawDefault) ? rawDefault : 'verticalCard';
  if (!enabled[defaultKey]) {
    defaultKey = order.find((key) => enabled[key]) ?? 'verticalCard';
  }

  return { enabled, order, defaultKey };
}

/** Default live config when resolve-batch has no dashboard entry. */
export function defaultCustomerDashboardLayoutConfig(
  lang: string,
  translate: (key: string) => string,
): CustomerDashboardLayoutConfig {
  return parseCustomerDashboardLayout(null, lang, translate);
}

/**
 * Maps Flexible Template `customerDashboard` payload into runtime display config.
 */
export function parseCustomerDashboardLayout(
  payload: Record<string, unknown> | null | undefined,
  lang: string,
  translate: (key: string) => string,
  device: CustomerDashboardLayoutDevice = 'desktop',
): CustomerDashboardLayoutConfig {
  const root = readRoot(payload, device);
  const kpisRaw = root?.['kpis'];
  const kpisRecord =
    kpisRaw && typeof kpisRaw === 'object' && !Array.isArray(kpisRaw)
      ? (kpisRaw as Record<string, unknown>)
      : {};

  const kpiOrder = parseKpiOrder(kpisRecord);

  const resolveKpiTitle = (key: DashboardKpiKey): string | null => {
    const saved = kpisRecord[key];
    const savedRecord =
      saved && typeof saved === 'object' && !Array.isArray(saved)
        ? (saved as Record<string, unknown>)
        : {};
    return resolveTitle(
      savedRecord['titleMl'],
      savedRecord['titleSource'] ?? KPI_DEFAULT_SOURCES[key],
      KPI_DEFAULT_TITLE_KEYS[key],
      lang,
      translate,
    );
  };

  const newItemsSaved = kpisRecord['newItems'];
  const newItemsRecord =
    newItemsSaved && typeof newItemsSaved === 'object' && !Array.isArray(newItemsSaved)
      ? (newItemsSaved as Record<string, unknown>)
      : {};
  const newItemsCtaEnabled =
    typeof newItemsRecord['ctaEnabled'] === 'boolean' ? newItemsRecord['ctaEnabled'] : true;
  const rawNewItemsTitleSource =
    typeof newItemsRecord['titleSource'] === 'string' ? newItemsRecord['titleSource'].trim() : '';
  const newItemsTitleSource = rawNewItemsTitleSource || KPI_DEFAULT_SOURCES.newItems;

  const readKpiTitleSource = (key: DashboardKpiKey): string => {
    const saved = kpisRecord[key];
    const savedRecord =
      saved && typeof saved === 'object' && !Array.isArray(saved)
        ? (saved as Record<string, unknown>)
        : {};
    const raw =
      typeof savedRecord['titleSource'] === 'string' ? savedRecord['titleSource'].trim() : '';
    return raw || KPI_DEFAULT_SOURCES[key];
  };
  const readKpiCtaEnabled = (key: DashboardKpiKey): boolean => {
    const saved = kpisRecord[key];
    const savedRecord =
      saved && typeof saved === 'object' && !Array.isArray(saved)
        ? (saved as Record<string, unknown>)
        : {};
    return typeof savedRecord['ctaEnabled'] === 'boolean' ? savedRecord['ctaEnabled'] : true;
  };
  const kpiTitleSources: Record<DashboardKpiKey, string> = {
    orders: readKpiTitleSource('orders'),
    shipments: readKpiTitleSource('shipments'),
    newItems: newItemsTitleSource,
  };
  const kpiCtaEnabled: Record<DashboardKpiKey, boolean> = {
    orders: readKpiCtaEnabled('orders'),
    shipments: readKpiCtaEnabled('shipments'),
    newItems: newItemsCtaEnabled,
  };

  const monthRaw = root?.['thisMonthOrder'];
  const monthRecord =
    monthRaw && typeof monthRaw === 'object' && !Array.isArray(monthRaw)
      ? (monthRaw as Record<string, unknown>)
      : {};
  const itemsDisplayed = Number(monthRecord['itemsDisplayed']);
  const monthOrderItemsDisplayed = MONTH_ORDER_ITEMS_OPTIONS.includes(itemsDisplayed)
    ? itemsDisplayed
    : 6;
  const monthOrderFilterType = isMonthOrderFilterType(monthRecord['filterType'])
    ? monthRecord['filterType']
    : 'orders';

  const calendarRaw = root?.['calendar'];
  const calendarRecord =
    calendarRaw && typeof calendarRaw === 'object' && !Array.isArray(calendarRaw)
      ? (calendarRaw as Record<string, unknown>)
      : {};

  const itemsRaw = root?.['items'];
  const itemsRecord =
    itemsRaw && typeof itemsRaw === 'object' && !Array.isArray(itemsRaw)
      ? (itemsRaw as Record<string, unknown>)
      : {};
  const viewPorts = parseItemsViewPorts(
    itemsRecord['itemsViewPorts'] ?? itemsRecord['viewPorts'],
  );

  const monthOrderTitle = resolveMlText(parseMlRecord(monthRecord['titleMl']), lang) || null;
  const calendarTitle = resolveMlText(parseMlRecord(calendarRecord['titleMl']), lang) || null;
  const itemsTitle = resolveMlText(parseMlRecord(itemsRecord['titleMl']), lang) || null;

  return {
    visible: root?.['visible'] !== false,
    kpiOrder,
    titles: {
      orders: resolveKpiTitle('orders'),
      shipments: resolveKpiTitle('shipments'),
      newItems: resolveKpiTitle('newItems'),
      monthOrder: monthOrderTitle,
      calendar: calendarTitle,
      items: itemsTitle,
    },
    newItemsCtaEnabled,
    newItemsTitleSource,
    kpiTitleSources,
    kpiCtaEnabled,
    monthOrderFilterType,
    monthOrderItemsDisplayed,
    itemsEnabledViewPorts: viewPorts.enabled,
    itemsDefaultViewPort: viewPorts.defaultKey,
    itemsViewPortOrder: viewPorts.order,
  };
}

/** Re-export defaults used by admin defaults for parity. */
export { DEFAULT_ALL_PRODUCTS_VIEW_PORTS };
